import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Settings, ArrowLeft, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getAuthToken } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InventoryManager = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [dealer, setDealer] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');

  useEffect(() => {
    checkAuthAndFetch();
  }, []);

  const checkAuthAndFetch = async () => {
    const token = getAuthToken();
    if (!token) {
      toast.error('Please log in to access inventory');
      navigate('/auth');
      return;
    }

    try {
      const profileRes = await axios.get(`${API}/dealer/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDealer(profileRes.data);
      setExternalUrl(profileRes.data.external_inventory_url || '');
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Please log in as a dealer');
        navigate('/auth');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const saveExternalUrl = async () => {
    const token = getAuthToken();
    try {
      await axios.put(`${API}/dealer/profile`, 
        { external_inventory_url: externalUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDealer({ ...dealer, external_inventory_url: externalUrl });
      setShowSettingsModal(false);
      toast.success('My Inventory link saved!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to save link');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#004E86]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="inventory-manager">
      <div className="fixed inset-0 bg-white z-40">
        {dealer?.external_inventory_url ? (
          <div className="h-full flex flex-col">
            {/* Simple Header */}
            <div className="bg-gray-100 p-2 flex items-center justify-between border-b shrink-0">
              <div className="flex items-center gap-4">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/dealer')}
                  className="flex items-center gap-1 text-[#004E86]"
                >
                  <ArrowLeft size={16} />
                  Back to Dashboard
                </Button>
                <div className="hidden sm:block h-6 w-px bg-gray-300" />
                <span className="text-sm font-semibold text-[#004E86] flex items-center gap-1">
                  <Package size={16} />
                  My Inventory
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowSettingsModal(true)}
              >
                <Settings size={14} className="mr-1" />
                Settings
              </Button>
            </div>
            <iframe
              src={dealer.external_inventory_url}
              className="flex-1 w-full border-0"
              title="My Inventory"
            />
          </div>
        ) : (
          <div className="h-full flex flex-col">
            <div className="bg-gray-100 p-2 flex items-center justify-between border-b shrink-0">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate('/dealer')}
                className="flex items-center gap-1 text-[#004E86]"
              >
                <ArrowLeft size={16} />
                Back to Dashboard
              </Button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center p-8 max-w-md">
                <Package size={64} className="mx-auto text-gray-300 mb-4" />
                <h2 className="text-2xl font-bold text-[#004E86] mb-4">Set Up My Inventory</h2>
                <p className="text-gray-600 mb-6">
                  Connect your external inventory management system to view it here.
                </p>
                <Button onClick={() => setShowSettingsModal(true)}>
                  <Settings size={16} className="mr-2" />
                  Configure Inventory Link
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>My Inventory Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="external-url">External Inventory URL</Label>
              <Input
                id="external-url"
                type="url"
                placeholder="https://your-inventory-system.com"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the URL of your inventory management website
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSettingsModal(false)}>
                Cancel
              </Button>
              <Button onClick={saveExternalUrl}>
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryManager;
